import { useState } from 'react';
import { LOGIN_METHOD_PATH } from '../constants/api';
import { AppConfig } from '../types/app';
import { profileRequest } from '../utils/apiHelper';
import logger from '../utils/logger';
import providersService from '../services/providers';
import { IdPs } from '../constants/providers';
import { rumService } from '../services/rum';
import { SimplifiedLoginRumEvent } from '../constants/metrics';
import { v4 as uuidv4 } from 'uuid';

export interface LoginMethodResponse {
  loginMethods: string[];
}

export async function getLoginMethod(
  config: AppConfig,
  email: string
): Promise<LoginMethodResponse> {
  const clientId = providersService.getClientId();
  const fetchOpts: RequestInit = {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      'X-Correlation-ID': uuidv4(),
    },
    body: JSON.stringify({
      emailID: email,
      clientID: clientId,
    }),
  };
  const options = {
    metricNs: 'GetLoginMethod',
    logWithRum: true,
    logWithKatal: true,
  };
  const response = await profileRequest(
    config,
    LOGIN_METHOD_PATH,
    fetchOpts,
    options,
    config.loginMethodProfileEndpoint
  );

  return await response.json();
}

function useLoginMethod(config: AppConfig) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>();
  const [loginMethods, setLoginMethods] = useState<string[] | undefined>();
  // Raw /loginMethod response (before OTP filtering), so the edge compares
  // against the same list the server returns.
  const [rawLoginMethods, setRawLoginMethods] = useState<
    string[] | undefined
  >();

  function fetchLoginMethod(email: string) {
    setIsLoading(true);
    setError(undefined);
    setLoginMethods(undefined);
    setRawLoginMethods(undefined);
    getLoginMethod(config, email)
      .then((result) => {
        if (!result?.loginMethods?.length) {
          throw new Error('No login method found for this email');
        }
        // Capture the raw, unfiltered response for the remember-login cookie.
        setRawLoginMethods(result.loginMethods);
        // For app clients flagged to hide OTP, drop OTP from the simplified-login
        // result. If OTP was the only method, fall back to AWS Builder ID so the
        // user is never left with no sign-in option.
        const clientId = providersService.getClientId();
        const idPsToHide = config.clients[clientId]?.idPsToHide ?? [];
        let effectiveMethods = result.loginMethods;

        if (
          idPsToHide.includes(IdPs.OTP) &&
          result.loginMethods.includes(IdPs.OTP)
        ) {
          const visibleMethods = result.loginMethods.filter(
            (method) => method !== IdPs.OTP
          );
          const fellBackToBuilderId = visibleMethods.length === 0;
          effectiveMethods = fellBackToBuilderId
            ? [IdPs.AWSBuilderID]
            : visibleMethods;

          rumService.recordEvent(SimplifiedLoginRumEvent.OTP_FILTERED, {
            clientId,
            fellBackToBuilderId,
            originalMethodCount: result.loginMethods.length,
            remainingMethodCount: effectiveMethods.length,
            url: window.location.href,
            path: `${window.location.pathname}${window.location.search}`,
          });
        }

        setLoginMethods(effectiveMethods);
      })
      .catch((err) => {
        logger.debug(err);
        setError(err);
      })
      .finally(() => setIsLoading(false));
  }

  function resetLoginMethods() {
    setLoginMethods(undefined);
    setRawLoginMethods(undefined);
    setError(undefined);
  }

  return {
    isLoading,
    error,
    loginMethods,
    rawLoginMethods,
    fetchLoginMethod,
    resetLoginMethods,
  };
}

export default useLoginMethod;
